import { idsByPhrasing, phrasingKey, type MatchableEntry, type MatchThresholds, type QuestionMatcher, type RankedEntry } from "./questionMatcher";
import { significantWords } from "./significantWords";

function overlap(a: Set<string>, b: Set<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  a.forEach((w) => {
    if (b.has(w)) shared += 1;
  });
  return shared / (a.size + b.size - shared);
}

/** Shares of words in common (Jaccard), 0–1. */
const WORD_OVERLAP_THRESHOLDS: MatchThresholds = {
  answer: 0.5,
  clearLead: 0.1,
  didYouMean: 0.3,
  related: 0.15,
};

/** Scores each entry by its best example's share of words with the text (Jaccard). */
export function createWordOverlapMatcher(
  entries: MatchableEntry[]
): QuestionMatcher {
  const examplesById = new Map(
    entries.map((e) => [e.id, e.examples.map(significantWords)])
  );
  const phrasings = idsByPhrasing(entries);
  const scoreAgainst = (text: string, allowedIds: string[]): RankedEntry[] => {
    const words = significantWords(text);
    const exactIds = phrasings.get(phrasingKey(text));
    return allowedIds
      .map((id) => ({
        id,
        score: Math.max(
          0,
          ...(examplesById.get(id) ?? []).map((ex) => overlap(words, ex))
        ),
        ...(exactIds?.has(id) ? { exact: true as const } : {}),
      }))
      .sort((a, b) => b.score - a.score);
  };
  return {
    ready: Promise.resolve(),
    loaded: true,
    thresholds: WORD_OVERLAP_THRESHOLDS,
    rank: async (text, allowedIds) => scoreAgainst(text, allowedIds),
    rankMany: async (requests) => requests.map(({ text, allowedIds }) => scoreAgainst(text, allowedIds)),
    scorePhrases: async (text, phrases) => {
      const words = significantWords(text);
      return phrases.map((phrase) => overlap(words, significantWords(phrase)));
    },
  };
}
